import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import "./AdminImageLinker.css";
import { useAuth } from "../api/AuthContext";
import { listExistingKeys, getPublicUrlForKey, getR2KeyFromUrl } from "../Utils/r2Upload";
import { extractBaseName, clusterByProximity } from "../Utils/imageMatching";

export default function AdminImageLinker() {
  const { api } = useAuth();
  const [allProducts, setAllProducts] = useState([]);
  const [unlinkedItems, setUnlinkedItems] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [productSearch, setProductSearch] = useState("");
  const [linkingProductId, setLinkingProductId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);

  const addLog = useCallback((message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev.slice(-49), { timestamp, message, type }]);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setSelectedKeys(new Set());
    try {
      addLog("Loading products...", "info");
      const res = await api.get("/products");
      const products = res.data?.data || (Array.isArray(res.data) ? res.data : []);
      setAllProducts(products);

      addLog("Checking Cloudflare R2 for uploaded images...", "info");
      const allKeys = await listExistingKeys("products/");

      const linkedKeys = new Set();
      for (const p of products) {
        const images = p.productImages || p.ProductImages || [];
        for (const url of images) {
          const key = getR2KeyFromUrl(url);
          if (key) linkedKeys.add(key);
        }
      }

      const unlinked = [...allKeys]
        .filter((key) => !linkedKeys.has(key))
        .map((key) => ({ key, baseName: extractBaseName(key), url: getPublicUrlForKey(key) }));

      setUnlinkedItems(unlinked);
      addLog(`Found ${unlinked.length} unlinked images out of ${allKeys.size} total in R2.`, "success");
    } catch (err) {
      addLog("Failed to load data: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [api, addLog]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clusters = useMemo(() => clusterByProximity(unlinkedItems), [unlinkedItems]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return allProducts;
    return allProducts.filter(
      (p) => String(p.productId).includes(q) || (p.productName || "").toLowerCase().includes(q)
    );
  }, [allProducts, productSearch]);

  const toggleSelect = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectCluster = (cluster) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      cluster.items.forEach((item) => next.add(item.key));
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const handleLinkToProduct = async (product) => {
    if (selectedKeys.size === 0) return;
    setLinkingProductId(product.productId);
    const keysToLink = [...selectedKeys];
    const urlsToAdd = keysToLink.map((k) => getPublicUrlForKey(k));
    const existingImages = product.productImages || product.ProductImages || [];
    const updatedImages = [...existingImages, ...urlsToAdd];

    try {
      await api.put(`/products/${product.productId}/images`, updatedImages);
      setAllProducts((prev) =>
        prev.map((p) => (p.productId === product.productId ? { ...p, productImages: updatedImages } : p))
      );
      const removedKeys = new Set(keysToLink);
      setUnlinkedItems((prev) => prev.filter((item) => !removedKeys.has(item.key)));
      setSelectedKeys(new Set());
      addLog(
        `Linked ${keysToLink.length} image(s) to #${product.productId} (${product.productName}).`,
        "success"
      );
    } catch (err) {
      addLog(`Failed to link to #${product.productId}: ${err.message}`, "error");
    } finally {
      setLinkingProductId(null);
    }
  };

  const handleUnlink = async (product, imageUrl) => {
    const existingImages = product.productImages || product.ProductImages || [];
    const updatedImages = existingImages.filter((u) => u !== imageUrl);

    try {
      await api.put(`/products/${product.productId}/images`, updatedImages);
      setAllProducts((prev) =>
        prev.map((p) => (p.productId === product.productId ? { ...p, productImages: updatedImages } : p))
      );
      const key = getR2KeyFromUrl(imageUrl);
      if (key) {
        setUnlinkedItems((prev) => [...prev, { key, baseName: extractBaseName(key), url: imageUrl }]);
      }
      addLog(`Removed an image from #${product.productId} (${product.productName}).`, "info");
    } catch (err) {
      addLog(`Failed to unlink: ${err.message}`, "error");
    }
  };

  return (
    <div className="li-container">
      <div className="li-wrapper">
        <header className="li-header">
          <div>
            <h1 className="li-title">Aminii Image Linker</h1>
            <p className="li-subtitle">Match unlinked Cloudflare photos to products</p>
          </div>
          <div className="li-header-actions">
            <Link to="/admin/bulk-upload" className="li-link-btn">
              ← Bulk Upload
            </Link>
            <button className="li-link-btn" onClick={loadData} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </header>

        <div className="li-howto">
          <span className="li-howto-step">
            <b>1.</b> Tap photo(s) below to select
          </span>
          <span className="li-howto-arrow">→</span>
          <span className="li-howto-step">
            <b>2.</b> Tap a product to link them there
          </span>
        </div>

        {selectedKeys.size > 0 && (
          <div className="li-selection-bar">
            <span>
              <b>{selectedKeys.size}</b> image(s) selected
            </span>
            <button className="li-clear-btn" onClick={clearSelection}>
              Clear
            </button>
            <span className="li-selection-hint">↓ Now tap a product below to link them ↓</span>
          </div>
        )}

        <div className="li-card">
          <h3 className="li-card-title">
            Unlinked Images <span className="li-count-badge">{unlinkedItems.length}</span>
          </h3>

          {loading ? (
            <p className="li-empty">Loading...</p>
          ) : clusters.length === 0 ? (
            <p className="li-empty">Nothing unlinked — every R2 image is matched to a product.</p>
          ) : (
            <div className="li-clusters">
              {clusters.map((cluster) => (
                <div className="li-cluster" key={cluster.rangeLabel}>
                  <div className="li-cluster-header">
                    <span className="li-cluster-label">
                      {cluster.rangeLabel} ({cluster.items.length})
                    </span>
                    <button className="li-select-all-btn" onClick={() => selectCluster(cluster)}>
                      Select all
                    </button>
                  </div>
                  <div className="li-thumb-grid">
                    {cluster.items.map((item) => (
                      <img
                        key={item.key}
                        src={item.url}
                        alt={item.baseName}
                        loading="lazy"
                        className={`li-thumb ${selectedKeys.has(item.key) ? "selected" : ""}`}
                        onClick={() => toggleSelect(item.key)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="li-card">
          <h3 className="li-card-title">Products</h3>
          <input
            type="text"
            className="li-search"
            placeholder="Search by product name or ID..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />

          {filteredProducts.length === 0 && !loading && (
            <p className="li-empty">
              {allProducts.length === 0 ? "No products loaded — try Refresh." : "No products match your search."}
            </p>
          )}

          <div className="li-product-list">
            {filteredProducts.map((product) => {
              const images = product.productImages || product.ProductImages || [];
              const isLinking = linkingProductId === product.productId;
              const canLink = selectedKeys.size > 0;

              return (
                <div className="li-product-row" key={product.productId}>
                  <div
                    className={`li-product-main ${canLink ? "linkable" : ""}`}
                    onClick={() => canLink && handleLinkToProduct(product)}
                  >
                    <div className="li-product-info">
                      <div className="li-product-name">{product.productName || `Product #${product.productId}`}</div>
                      <div className="li-product-meta">
                        #{product.productId} · {images.length} image{images.length === 1 ? "" : "s"} linked
                      </div>
                    </div>
                    {isLinking && <span className="li-linking-spinner">Linking...</span>}
                    {canLink && !isLinking && <span className="li-link-here-badge">Tap to link here</span>}
                  </div>

                  {/* Already-linked images always visible, so it's always clear what's on this product */}
                  <div className="li-product-images">
                    {images.length === 0 ? (
                      <span className="li-no-images">No images linked yet</span>
                    ) : (
                      images.map((url, idx) => (
                        <div className="li-product-image-item" key={idx}>
                          <img src={url} alt="" className="li-product-image-thumb" />
                          <button
                            className="li-unlink-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnlink(product, url);
                            }}
                            title="Remove this image from this product"
                          >
                            ×
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="li-card">
          <h3 className="li-card-title">Activity Log</h3>
          <div className="li-log-container">
            {logs.length === 0 ? (
              <div className="li-empty">No activity yet.</div>
            ) : (
              logs.map((log, idx) => (
                <div key={idx} className={`li-log-item li-log-${log.type}`}>
                  <span>[{log.timestamp}]</span> <span>{log.message}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
